import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn (...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Utility function to check if JWT token is expired or about to expire
export function isTokenExpired (token: string, bufferMinutes: number = 5): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    const bufferTime = bufferMinutes * 60; // Convert minutes to seconds

    return (expirationTime - currentTime) <= bufferTime;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // If we can't parse it, consider it expired
  }
}

// Utility function to proactively refresh token if needed
export async function ensureValidToken (): Promise<boolean> {
  const token = localStorage.getItem('authToken');

  if (!token) {
    return false;
  }

  // Check if token is expired or about to expire (within 5 minutes)
  if (isTokenExpired(token, 5)) {
    console.log('🔄 Token is about to expire, refreshing proactively...');

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_LOCATION === "production" ? process.env.REACT_APP_PRODUCTION_BACKEND_URL : process.env.REACT_APP_LOCAL_BACKEND_URL;
      if (!baseUrl) {
        console.error('Backend URL is not set');
        throw new Error('Backend URL is not set');
      }

      const refreshResponse = await fetch(baseUrl + 'refresh-token', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        if (refreshData.success && refreshData.token) {
          localStorage.setItem('authToken', refreshData.token);
          console.log('✅ Token refreshed proactively');
          return true;
        }
      }
    } catch (error) {
      console.error('❌ Proactive token refresh failed:', error);
    }

    // If refresh failed, clear the token
    localStorage.removeItem('authToken');
    return false;
  }

  return true; // Token is still valid
}

export async function axFetch (method: string, url: string, body: any) {
  const baseUrl = process.env.REACT_APP_BACKEND_LOCATION === "production" ? process.env.REACT_APP_PRODUCTION_BACKEND_URL : process.env.REACT_APP_LOCAL_BACKEND_URL;
  if (!baseUrl) {
    console.error('Backend URL is not set');
    throw new Error('Backend URL is not set');
  }

  // Proactively check and refresh token if needed (skip for auth endpoints)
  if (url !== 'sign-up' && url !== 'sign-in' && url !== 'refresh-token' && url !== 'contact-us') {
    const tokenValid = await ensureValidToken();
    if (!tokenValid) {
      console.log('🚪 No valid token available');
      return new Response(JSON.stringify({ error: 'Authentication required' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  // Check if body is FormData
  const isFormData = body instanceof FormData;

  const fetchOptions: RequestInit = {
    method: method,
    body: isFormData ? body : JSON.stringify(body)
  };

  // Set headers
  const headers: Record<string, string> = {};

  // Add Content-Type header for JSON data
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  // Add Authorization header with JWT token if available
  const token = localStorage.getItem('authToken');
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  fetchOptions.headers = headers;

  const response = await fetch(baseUrl + url, fetchOptions);

  // List of expensive operations that should not be retried to prevent duplicate processing
  const expensiveOperations = [
    're-comprehensive-analysis',
    'generate-due-diligence',
    'generate-enhanced-due-diligence',
    'financial-analysis',
    'intake-dataroom'
  ];

  if (response.status === 403) {
    // Clone the response to read the body without consuming it
    const responseClone = response.clone();

    try {
      const responseText = await responseClone.text();
      console.log('403 Error response text:', responseText);

      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        console.log('Could not parse as JSON, trying text match');
        // If it's not JSON, check if the text contains the error message
        if (responseText.includes('Account has expired')) {
          errorData = { error: 'Account has expired' };
        } else {
          throw new Error('Response is not JSON and does not contain expected error');
        }
      }

      console.log('403 Error data:', errorData);

      if (errorData.error === 'Account has expired') {
        console.log('Account expired detected!');
        const token = localStorage.getItem('authToken');
        console.log('Token exists?', !!token);
        if (token) {
          localStorage.removeItem('authToken');
          console.log('Token removed');
        }
        // Always dispatch event regardless of token (user might be on sign-in page)
        console.log('Dispatching accountExpired event...');
        const event = new CustomEvent('accountExpired', { bubbles: true, cancelable: true });
        window.dispatchEvent(event);
        console.log('accountExpired event dispatched, defaultPrevented:', event.defaultPrevented);
        return response;
      }
    } catch (parseError) {
      console.log('Could not parse error response:', parseError);
    }

    console.log('Token expired, attempting to refresh...');

    try {
      // Try to refresh the token
      const refreshResponse = await fetch(baseUrl + 'refresh-token', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        if (refreshData.success && refreshData.token) {
          // Store the new token
          localStorage.setItem('authToken', refreshData.token);
          console.log('Token refreshed successfully');

          // Retry the original request with the new token
          const newHeaders = { ...headers };
          newHeaders["Authorization"] = `Bearer ${refreshData.token}`;

          const retryResponse = await fetch(baseUrl + url, {
            ...fetchOptions,
            headers: newHeaders
          });

          return retryResponse;
        }
      }
    } catch (refreshError) {
      console.error('❌ Token refresh failed:', refreshError);
    }

    // If refresh failed, clear the token and redirect to login
    localStorage.removeItem('authToken');
    console.log('❌ Token refresh failed, clearing auth token');
  }

  return response;
}

// Utility function to format metric values with K, M, B notation
export function formatMetricValue (value: string, format: string = "number"): string {
  const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));

  if (isNaN(numValue)) {
    return value; // Return original if not a number
  }

  if (format?.toLowerCase().includes('percentage')) {
    return `${(numValue * 100).toFixed(2)}%`;
  }

  // Check if this is a currency value
  const isCurrency = format?.toLowerCase().includes('currency')

  let formattedNumber = '';

  if (isCurrency) {
    // For currency, use full numbers with commas (no decimals)
    formattedNumber = Math.round(numValue).toLocaleString();
  } else {
    // Format with K, M, B notation for non-currency
    if (numValue >= 1000000000) {
      formattedNumber = `${(numValue / 1000000000).toFixed(1)}B`;
    } else if (numValue >= 1000000) {
      formattedNumber = `${(numValue / 1000000).toFixed(1)}M`;
    } else if (numValue >= 100000) {
      formattedNumber = `${(numValue / 1000).toFixed(1)}K`;
    } else {
      formattedNumber = numValue.toFixed(0);
    }
  }

  // Add dollar sign for currency values
  return isCurrency ? `$${formattedNumber}` : formattedNumber;
};